import type {
  BusinessMetricDictionaryReadModel,
  BusinessMetricReadModel,
  BusinessMetricReportBundleReadModel,
} from "@billiard/contracts";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Download, RefreshCw } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import type { OperatorIdentity } from "./api";
import {
  businessMetricDisplay,
  businessMetricDrillHref,
  businessMetricReportBundleQueryKey,
  businessMetricReportXlsxBytes,
  getBusinessMetricReportBundle,
} from "./businessMetricDictionary";

export function businessMetricReportMatchesSelection(
  report: BusinessMetricReportBundleReadModel | undefined,
  operator: Pick<OperatorIdentity, "tenantId" | "storeId">,
  businessDate: string,
): report is BusinessMetricReportBundleReadModel {
  return Boolean(
    report &&
      report.tenantId === operator.tenantId &&
      report.storeId === operator.storeId &&
      report.businessDate === businessDate &&
      report.dictionary.tenantId === operator.tenantId &&
      report.dictionary.storeId === operator.storeId &&
      report.dictionary.businessDate === businessDate,
  );
}

export type BusinessMetricDownloadFormat = "CSV" | "XLSX";

function saveBusinessMetricBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
}

export function saveBusinessMetricReport(
  report: BusinessMetricReportBundleReadModel,
  format: BusinessMetricDownloadFormat,
): void {
  if (format === "CSV") {
    saveBusinessMetricBlob(
      new Blob([report.csv], { type: "text/csv;charset=utf-8" }),
      report.csvFileName,
    );
    return;
  }
  saveBusinessMetricBlob(
    new Blob([businessMetricReportXlsxBytes(report)], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }),
    report.xlsxFileName,
  );
}

function businessMetricReportIdentity(
  report: BusinessMetricReportBundleReadModel,
  format: BusinessMetricDownloadFormat,
): string {
  return [
    report.tenantId,
    report.storeId,
    report.businessDate,
    report.generatedAt,
    format,
    format === "CSV" ? report.csvSha256 : report.xlsxSha256,
  ].join("\u0000");
}

function metricDrillUnavailableLabel(metricId: BusinessMetricReadModel["id"]): string {
  if (metricId === "ORDER_RECEIVABLE") return "当前没有可安全钻取的订单应收明细";
  if (metricId === "PRODUCT_COST") return "当前没有可安全钻取的冻结成本明细";
  if (metricId === "COMMISSION_PAYABLE") return "当前没有可安全钻取的预计提成明细";
  if (metricId === "CASH_DIFFERENCE") return "须完成全部交班后，才能按班次核对现金差异";
  return "当前没有可安全钻取的本项明细";
}

function MetricItem({
  metric,
  businessDate,
}: {
  metric: BusinessMetricReadModel;
  businessDate: string;
}): React.JSX.Element {
  const display = businessMetricDisplay(metric);
  const drillHref = businessMetricDrillHref(
    metric.drillTarget,
    businessDate,
    metric.id,
    metric.availability,
  );
  return (
    <article
      className={`business-metric-item availability-${metric.availability.toLowerCase().replaceAll("_", "-")}`}
      data-metric-id={metric.id}
    >
      <div className="business-metric-item-heading">
        <div>
          <h3>{metric.label}</h3>
          <strong>{display.valueLabel}</strong>
        </div>
        <span>{display.availabilityLabel}</span>
      </div>
      <dl>
        <div>
          <dt>会发生什么</dt>
          <dd>{metric.explanation}</dd>
        </div>
        <div>
          <dt>数据来源</dt>
          <dd>{metric.sourceLabel}</dd>
        </div>
        <div>
          <dt>当前原因</dt>
          <dd>{display.reason ?? "本项已从上述来源完整取得。"}</dd>
        </div>
      </dl>
      {drillHref === null ? (
        <span className="business-metric-drill-unavailable">
          {metricDrillUnavailableLabel(metric.id)}
        </span>
      ) : (
        <Link className="business-metric-drill-link" to={drillHref}>
          查看{metric.sourceLabel}明细
        </Link>
      )}
    </article>
  );
}

export function BusinessMetricDictionaryPanel({
  operator,
  businessDate,
  loader = getBusinessMetricReportBundle,
  saveReport = saveBusinessMetricReport,
}: {
  operator: OperatorIdentity;
  businessDate: string;
  loader?: typeof getBusinessMetricReportBundle;
  saveReport?: (
    report: BusinessMetricReportBundleReadModel,
    format: BusinessMetricDownloadFormat,
  ) => void;
}): React.JSX.Element {
  const [downloadFeedback, setDownloadFeedback] = useState<
    Partial<
      Record<
        BusinessMetricDownloadFormat,
        {
          reportIdentity: string;
          kind: "SUCCESS" | "ERROR";
          message: string;
        }
      >
    >
  >({});
  const context = { tenantId: operator.tenantId, storeId: operator.storeId };
  const report = useQuery({
    queryKey: businessMetricReportBundleQueryKey(context, businessDate),
    queryFn: ({ signal }) => loader(businessDate, context, signal),
    enabled: Boolean(businessDate),
    retry: false,
    refetchInterval: 5_000,
  });
  const visibleReport =
    !report.isError && businessMetricReportMatchesSelection(report.data, operator, businessDate)
      ? report.data
      : undefined;
  const responseScopeChanged = Boolean(
    report.data && !businessMetricReportMatchesSelection(report.data, operator, businessDate),
  );

  if (!businessDate) {
    return (
      <section className="business-metric-dictionary-status" aria-live="polite">
        <strong>正在确认核账营业日</strong>
        <span>营业日确认后再读取指标，当前不会显示其他日期的金额。</span>
      </section>
    );
  }
  if (!visibleReport) {
    const failed = report.isError || responseScopeChanged;
    return (
      <section
        className={`business-metric-dictionary-status ${failed ? "is-error" : ""}`}
        role={failed ? "alert" : "status"}
        aria-live="polite"
      >
        {failed ? (
          <AlertTriangle size={20} aria-hidden="true" />
        ) : (
          <RefreshCw className="spin" size={20} aria-hidden="true" />
        )}
        <div>
          <strong>{failed ? "指标口径暂时无法核对" : "正在读取指标口径与来源"}</strong>
          <span>
            {failed
              ? "旧日期或旧门店的指标已隐藏，请恢复连接后重新读取。"
              : `正在读取 ${businessDate} 的权威指标，不会沿用上一日期的金额。`}
          </span>
        </div>
        {failed ? (
          <button
            className="button secondary compact"
            type="button"
            onClick={() => report.refetch()}
          >
            <RefreshCw size={16} aria-hidden="true" /> 重新读取指标
          </button>
        ) : null}
      </section>
    );
  }

  const visibleDictionary: BusinessMetricDictionaryReadModel = visibleReport.dictionary;
  const funds = visibleDictionary.metrics.slice(0, 6);
  const operations = visibleDictionary.metrics.slice(6);
  const download = (format: BusinessMetricDownloadFormat): void => {
    const identity = businessMetricReportIdentity(visibleReport, format);
    const fileName = format === "CSV" ? visibleReport.csvFileName : visibleReport.xlsxFileName;
    try {
      saveReport(visibleReport, format);
      setDownloadFeedback((current) => ({
        ...current,
        [format]: {
          reportIdentity: identity,
          kind: "SUCCESS",
          message: `已开始下载${format}文件“${fileName}”。`,
        },
      }));
    } catch {
      setDownloadFeedback((current) => ({
        ...current,
        [format]: {
          reportIdentity: identity,
          kind: "ERROR",
          message: `${format} 文件未开始下载，请检查浏览器下载权限后重试。`,
        },
      }));
    }
  };
  return (
    <section className="business-metric-dictionary" aria-label="指标口径与来源">
      <div className="business-metric-dictionary-heading">
        <div>
          <span className="eyebrow">营业日 {visibleDictionary.businessDate}</span>
          <h2>指标口径与来源</h2>
          <p>
            每项分别来自对应账本，不会互相抵消。单项暂不可用表示当前没有可信事实，不代表金额或数量为零，也不表示整套
            ERP 离线。
          </p>
        </div>
        <div className="business-metric-report-actions">
          <span>
            {visibleDictionary.coverage === "MISSING_HISTORY" ? "历史资料缺失" : "按记录事实展示"}
          </span>
          <time dateTime={visibleReport.generatedAt}>
            生成于　
            {new Date(visibleReport.generatedAt).toLocaleString("zh-CN", {
              hour12: false,
              timeZone: visibleDictionary.timeZone,
            })}
          </time>
          <button
            className="button secondary compact business-metric-download"
            type="button"
            onClick={() => download("CSV")}
          >
            <Download size={16} aria-hidden="true" /> 下载本页中文 CSV
          </button>
          <button
            className="button secondary compact business-metric-download"
            type="button"
            onClick={() => download("XLSX")}
          >
            <Download size={16} aria-hidden="true" /> 下载本页中文 XLSX
          </button>
          <small>下载文件与本页是同一份数据，不会重新取数或改写金额。</small>
          {(["CSV", "XLSX"] as const).map((format) => {
            const feedback = downloadFeedback[format];
            return feedback?.reportIdentity ===
              businessMetricReportIdentity(visibleReport, format) ? (
              <small
                key={format}
                className={feedback.kind === "ERROR" ? "form-error" : "success-text"}
                role={feedback.kind === "ERROR" ? "alert" : "status"}
              >
                {feedback.message}
              </small>
            ) : null;
          })}
        </div>
      </div>
      <div className="business-metric-group">
        <div>
          <h3>六类资金分别核对</h3>
          <p>应收、实收、储值本金、赠送金、退款和挂账各自展示，不能相互冲抵。</p>
        </div>
        <div className="business-metric-grid">
          {funds.map((metric) => (
            <MetricItem
              key={metric.id}
              metric={metric}
              businessDate={visibleDictionary.businessDate}
            />
          ))}
        </div>
      </div>
      <div className="business-metric-group">
        <div>
          <h3>经营成本与现场核对</h3>
          <p>商品成本、提成、库存和现金差异保持各自来源与可用状态。</p>
        </div>
        <div className="business-metric-grid">
          {operations.map((metric) => (
            <MetricItem
              key={metric.id}
              metric={metric}
              businessDate={visibleDictionary.businessDate}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
